'use client'

import { useState } from 'react'
import { signIn } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Card from '@/components/ui/Card'
import { useToast } from '@/components/ui/ToastContainer'
import { parseApiError, getUserFriendlyMessage, logError } from '@/utils/error-handler'

export default function SignIn() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const { showToast } = useToast()
  const registered = searchParams.get('registered')
  const verify = searchParams.get('verify')
  
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [errorDetails, setErrorDetails] = useState<any>(null)
  const [loading, setLoading] = useState(false) 
  const [showPassword, setShowPassword] = useState(false)
  const [showEmailVerification, setShowEmailVerification] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setErrorDetails(null)
    setLoading(true)

    try {
      const result = await signIn('credentials', {
        email,
        password,
        redirect: false,
      })

      if (result?.error) {
        // Check if it's an email verification error
        if (result.error === 'EMAIL_NOT_VERIFIED') {
          setShowEmailVerification(true)
          setError('')
          setErrorDetails(null)
          return
        }

        // Parse the error from NextAuth
        const parsedError = parseApiError(result.error)
        const userFriendlyMessage = getUserFriendlyMessage(parsedError)
        
        setError(userFriendlyMessage)
        setErrorDetails(parsedError)
        
        logError(parsedError, {
          component: 'SignIn',
          action: 'signIn',
          additionalInfo: { email }
        })
        
        showToast({
          type: 'error',
          title: 'Sign In Failed',
          message: userFriendlyMessage,
        })
      } else {
        showToast({
          type: 'success',
          title: 'Welcome Back!',
          message: 'Redirecting to your dashboard...',
          duration: 2000,
        })
        setTimeout(() => {
          router.push('/dashboard')
          router.refresh()
        }, 500)
      }
    } catch (err) {
      const parsedError = parseApiError(err)
      const userFriendlyMessage = getUserFriendlyMessage(parsedError)
      
      setError(userFriendlyMessage)
      setErrorDetails(parsedError)
      
      logError(parsedError, {
        component: 'SignIn',
        action: 'signIn',
        additionalInfo: { email }
      })
      
      showToast({
        type: 'error',
        title: 'Sign In Failed',
        message: userFriendlyMessage,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    if (!email) {
      showToast({
        type: 'error',
        title: 'Email Required',
        message: 'Please enter your email address first.',
      })
      return
    }

    setResendLoading(true)
    try {
      const response = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      })

      const data = await response.json()

      if (response.ok) {
        showToast({
          type: 'success',
          title: 'Verification Email Sent',
          message: 'Please check your email for the verification link.',
        })
      } else {
        showToast({
          type: 'error',
          title: 'Failed to Send Email',
          message: data.message || 'Please try again later.',
        })
      }
    } catch (error) {
      showToast({
        type: 'error',
        title: 'Error',
        message: 'Failed to send verification email. Please try again.',
      })
    } finally {
      setResendLoading(false)
    }
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md animate-scale-in">
        <Card>
          <div className="text-center mb-8">
            <div className="text-6xl mb-4">🎣</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-600">Sign in to your Fishing Club account</p>
          </div>

          {registered && (
            <div className="mb-6 p-4 bg-teal-50 border border-teal-200 rounded-lg text-teal-800 animate-slide-down">
              {verify ? (
                <>
                  ✓ Account created successfully! 
                  <div className="mt-2 text-sm">
                    Please check your email for a verification link. You must verify your email before you can sign in.
                  </div>
                </>
              ) : (
                '✓ Account created successfully! Please sign in.'
              )}
            </div>
          )}

          {error && (
            <div className="mb-6 animate-slide-down">
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <span className="block">{error}</span>
                  {errorDetails?.requestId && (
                    <span className="block text-xs text-red-600 mt-1">
                      Request ID: <code className="bg-red-100 px-1 rounded">{errorDetails.requestId}</code>
                    </span>
                  )}
                </div>
              </div>

              {/* Enhanced error details - always shown for full diagnostics */}
              {errorDetails && (
                <details className="mt-2 text-left bg-red-100 border border-red-300 rounded-lg p-3">
                  <summary className="cursor-pointer font-semibold text-red-900 mb-2 text-sm">
                    Debug Information
                  </summary>
                  <div className="space-y-2 text-xs">
                    {errorDetails.code && (
                      <div>
                        <strong className="text-red-800">Error Code:</strong>
                        <span className="ml-2 text-red-700">{errorDetails.code}</span>
                      </div>
                    )}
                    {errorDetails.timestamp && (
                      <div>
                        <strong className="text-red-800">Timestamp:</strong>
                        <span className="ml-2 text-red-700">{errorDetails.timestamp}</span>
                      </div>
                    )}
                    {errorDetails.stack && (
                      <div>
                        <strong className="text-red-800">Stack Trace:</strong>
                        <pre className="text-red-700 mt-1 p-2 bg-red-200 rounded overflow-auto max-h-32 text-xs">
                          {errorDetails.stack}
                        </pre>
                      </div>
                    )}
                    {errorDetails.details && (
                      <div>
                        <strong className="text-red-800">Additional Details:</strong>
                        <pre className="text-red-700 mt-1 p-2 bg-red-200 rounded overflow-auto text-xs">
                          {JSON.stringify(errorDetails.details, null, 2)}
                        </pre>
                      </div>
                    )}
                  </div>
                </details>
              )}
            </div>
          )}

          {showEmailVerification && (
            <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 animate-slide-down">
              <div className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div className="flex-1">
                  <h3 className="font-semibold mb-2">Email Verification Required</h3>
                  <p className="text-sm mb-3">
                    Please verify your email address before signing in. Check your inbox for a verification link.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleResendVerification}
                      disabled={resendLoading || !email}
                      className="bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200"
                    >
                      {resendLoading ? (
                        <span className="flex items-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </span>
                      ) : (
                        'Resend Verification Email'
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowEmailVerification(false)}
                      className="text-yellow-700 hover:bg-yellow-100"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <Input
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />

            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
              size="lg"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing in...
                </span>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-3">
            <p className="text-gray-600">
              Don&apos;t have an account?{' '}
              <Link href="/auth/signup" className="text-ocean-600 hover:text-ocean-700 font-medium transition-colors">
                Sign up
              </Link>
            </p>
            <p className="text-gray-600">
              <Link href="/auth/forgot-password" className="text-ocean-600 hover:text-ocean-700 font-medium transition-colors">
                Forgot your password?
              </Link>
            </p>
            <Link href="/" className="block text-gray-500 hover:text-gray-700 transition-colors">
              ← Back to home
            </Link>
          </div>
        </Card>
      </div>
    </div>
  )
}
